<template>
    <div class="l-infinite-scroll">
        <div v-show="Array.isArray(list) && list.length">
            <ul ref="list" :class="listClass" data-test-id="infinite-scroll-list">
                <li
                    v-for="(item, index) in list"
                    :key="index"
                    :class="itemClass"
                    data-test-class="infinite-scroll-list-item"
                    tabindex="0"
                    @click="onItemClick(item, index)"
                    @keyup.enter="onItemClick(item, index)"
                >
                    <slot name="item" :item="item" :index="index"></slot>
                </li>
            </ul>

            <div v-show="canLoadMore" class="tw-mt-4">
                <l-base-button
                    ref="loadMoreButton"
                    :button-types="['primary', 'framed', 'inline', 'size-sm']"
                    :is-loading="isLoadingNewListItems"
                    data-test-class="load-more"
                    @click.native="onTriggerLoadMore"
                >
                    {{ $t("genericActions.loadMore") }}
                </l-base-button>
            </div>
        </div>
        <div v-show="(!list || !list.length) && noDataShow" data-test-id="infinite-scroll-no-data">
            <l-no-data
                :icon="noDataIcon"
                :text="noDataText"
                :btn-show="noDataBtnShow"
                :btn-text="noDataBtnText"
                :btn-handler="noDataBtnHandler"
            />
        </div>
    </div>
</template>

<script>
import throttle from "lodash.throttle";

import LNoData from "@/components/LNoData";
import LBaseButton from "@/components/LBaseButton";

export default {
    components: {
        LNoData,
        LBaseButton,
    },
    props: {
        list: {
            type: Array,
            required: true,
        },
        onListScroll: {
            type: Function,
            default: () => {},
        },
        onListBottom: {
            type: Function,
            default: () => {},
        },
        onItemClick: {
            type: Function,
            default: () => {},
        },
        attachToWindowScroll: {
            type: Boolean,
            default: true,
        },
        listClass: {
            type: String,
            default: "list",
        },
        itemClass: {
            type: String,
            default: "item",
        },
        noDataShow: {
            type: Boolean,
            default: true,
        },
        noDataIcon: {
            type: String,
            default: undefined,
        },
        noDataText: {
            type: String,
            default: "",
        },
        noDataBtnShow: {
            type: Boolean,
            default: false,
        },
        noDataBtnText: {
            type: String,
            default: "",
        },
        noDataBtnHandler: {
            type: Function,
            default: () => {},
        },
        canLoadMore: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            observer: null,
            isLoadingNewListItems: false,
        };
    },
    watch: {
        list() {
            this.isLoadingNewListItems = false;
        },
    },
    mounted() {
        this.$nextTick(() => {
            this.registerObserver();
        });
    },
    beforeDestroy() {
        try {
            window.removeEventListener("scroll", this.onWindowScroll);
            this.$refs.list.removeEventListener("scroll", this.onRefListScroll);
            this.observer.disconnect();
        } catch {
            // nothing to do, observer doesn't exist
        }
    },
    methods: {
        registerObserver() {
            try {
                this.observer = new IntersectionObserver(([entry]) => {
                    if (
                        entry &&
                        entry.isIntersecting &&
                        this.canLoadMore &&
                        !this.isLoadingNewListItems
                    ) {
                        this.onTriggerLoadMore();
                    }
                });

                this.observer.observe(this.$refs.loadMoreButton?.$el);
            } catch {
                // fallback logic if IntersectionObserver is not supported
                if (this.attachToWindowScroll) {
                    window.addEventListener("scroll", this.onWindowScroll, false);
                } else {
                    this.$refs.list.addEventListener("scroll", this.onRefListScroll);
                }
            }
        },
        onWindowScroll() {
            const isBottom =
                document.documentElement.scrollHeight ===
                document.documentElement.scrollTop + window.innerHeight;

            if (isBottom) {
                this.onTriggerLoadMore();
            }
        },
        onRefListScroll(e) {
            const { scrollTop, scrollHeight, clientHeight } = e.target;

            const isBottom = scrollHeight === scrollTop + clientHeight;

            if (isBottom) {
                this.onTriggerLoadMore();
            }
        },
        onTriggerLoadMore() {
            this.isLoadingNewListItems = true;

            // as this relies on the implementation of the parent, we add a max wait time as fallback
            setTimeout(() => {
                this.isLoadingNewListItems = false;
            }, 3000);

            this.onLoadMore();
        },
        onLoadMore: throttle(function() {
            this.onListBottom();
        }, 1000),
    },
};
</script>

<style scoped>
.l-infinite-scroll ul {
    list-style: none;
    padding: 0;
    margin: 0;
}
</style>
